import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Code2, Users, Hash, Cpu } from 'lucide-react';
import { useObsClaudeCode } from '@/hooks/useObservability';
import { StatCard, LoaderBlock, fmtUSD, fmtNum, fmtMonth } from './shared';

const COLORS = ['#8b5cf6','#3b82f6','#10b981','#f59e0b','#ef4444','#06b6d4','#a855f7','#ec4899'];

export function ObsClaudeCodeTab() {
  const { t } = useTranslation();
  const { data: rows = [], isLoading } = useObsClaudeCode();

  const byIdentity = useMemo(() => {
    const map = new Map<string, { id: string; usd: number; sessions: number; lines: number; commits: number; tokens: number; modes: Set<string> }>();
    for (const r of rows) {
      const cur = map.get(r.identity) || { id: r.identity, usd: 0, sessions: 0, lines: 0, commits: 0, tokens: 0, modes: new Set<string>() };
      cur.usd += r.estimated_cost_usd || 0;
      cur.sessions += r.num_sessions || 0;
      cur.lines += r.lines_added || 0;
      cur.commits += r.commits_by_claude_code || 0;
      cur.tokens += r.total_tokens || 0;
      if (r.actor_type) cur.modes.add(r.actor_type);
      map.set(r.identity, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.usd - a.usd);
  }, [rows]);

  const byModel = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      if (!r.model) continue;
      map.set(r.model, (map.get(r.model) || 0) + (r.estimated_cost_usd || 0));
    }
    return Array.from(map.entries())
      .map(([model, usd]) => ({ model, usd }))
      .sort((a, b) => b.usd - a.usd);
  }, [rows]);

  const byDate = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const d = (r.activity_date || '').substring(0, 7);
      if (!d) continue;
      map.set(d, (map.get(d) || 0) + (r.estimated_cost_usd || 0));
    }
    return Array.from(map.entries())
      .map(([month, usd]) => ({ month, usd }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [rows]);

  if (isLoading) return <LoaderBlock />;

  const totalUsd = byIdentity.reduce((s, r) => s + r.usd, 0);
  const totalSessions = byIdentity.reduce((s, r) => s + r.sessions, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Code2} tone="purple" label={t('observability.claude_code.stat_ytd_cost')} value={`$${Math.round(totalUsd)}`}
                  sub={t('observability.claude_code.stat_ytd_cost_sub')} />
        <StatCard icon={Users} tone="blue" label={t('observability.claude_code.stat_devs')} value={fmtNum(byIdentity.length)} />
        <StatCard icon={Hash} tone="green" label={t('observability.claude_code.stat_sessions')} value={fmtNum(totalSessions)} />
        <StatCard icon={Cpu} tone="amber" label={t('observability.claude_code.stat_top_model')} value={byModel[0]?.model.substring(7, 20) || '—'}
                  sub={byModel[0] ? `$${Math.round(byModel[0].usd)}` : undefined} />
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('observability.claude_code.monthly_cost_title')}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={byDate}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} />
            <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} />
            <Tooltip formatter={(v: any) => `$${Math.round(v)}`} labelFormatter={(m) => fmtMonth(String(m))} />
            <Line type="monotone" dataKey="usd" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('observability.claude_code.cost_by_model_title')}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={byModel} margin={{ left: 60, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="model" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} />
            <Tooltip formatter={(v: any) => `$${Math.round(v)}`} />
            <Bar dataKey="usd" radius={[4, 4, 0, 0]}>
              {byModel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5 overflow-auto">
        <h3 className="text-sm font-semibold mb-3">{t('observability.claude_code.dev_ranking_title')}</h3>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3 w-8">{t('observability.claude_code.col_rank')}</th>
              <th className="pr-3">{t('observability.claude_code.col_identity')}</th>
              <th className="pr-3">{t('observability.claude_code.col_modes')}</th>
              <th className="pr-3 text-right">{t('observability.claude_code.col_usd')}</th>
              <th className="pr-3 text-right">{t('observability.claude_code.col_sessions')}</th>
              <th className="pr-3 text-right">{t('observability.claude_code.col_lines')}</th>
              <th className="pr-3 text-right">{t('observability.claude_code.col_commits')}</th>
              <th className="text-right">{t('observability.claude_code.col_tokens')}</th>
            </tr>
          </thead>
          <tbody>
            {byIdentity.map((r, i) => (
              <tr key={r.id} className="border-b hover:bg-muted/50">
                <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                <td className="pr-3 text-xs">{r.id}</td>
                <td className="pr-3 text-xs">
                  {Array.from(r.modes).map((m) => (
                    <span key={m} className="text-xs px-1.5 py-0.5 rounded bg-muted mr-1">{m.replace('_actor', '')}</span>
                  ))}
                </td>
                <td className="pr-3 text-right font-mono font-semibold tabular-nums">{fmtUSD(r.usd)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.sessions)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.lines)}</td>
                <td className="pr-3 text-right text-xs">{fmtNum(r.commits)}</td>
                <td className="text-right text-xs">{(r.tokens / 1e6).toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
